/**
 * useModal Hook
 * Simplifies modal state management
 * Replaces scattered boolean states across 32+ components
 */

import { useState, useCallback } from 'react';

/**
 * Modal state hook
 *
 * @param {boolean} defaultVisible - Initial visibility state
 * @returns {object} - { visible, open, close, toggle, data, setData }
 *
 * @example
 * const modal = useModal();
 * <Modal visible={modal.visible} onCancel={modal.close}>
 *   Content
 * </Modal>
 * <Button onClick={() => modal.open({ id: 123 })}>Open</Button>
 */
function useModal(defaultVisible = false) {
  const [visible, setVisible] = useState(defaultVisible);
  const [data, setData] = useState(null);

  const open = useCallback((modalData = null) => {
    setData(modalData);
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    // Clear data after animation completes
    setTimeout(() => setData(null), 300);
  }, []);

  const toggle = useCallback(() => {
    setVisible((prev) => !prev);
  }, []);

  return {
    visible,
    open,
    close,
    toggle,
    data,
    setData,
  };
}

export default useModal;
